import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import savingsPlans from "@/services/SavingsPlanService";
import { buildMapNavigationState } from "@/screen/Map/resolveContractOnboardingStepNavigation";
import { isReentrantContractStepKey } from "@/screen/Map/contractReentrantSteps";

export interface ContractStepGuardResult
{
    isChecking: boolean;
    /**
     * Clave del paso vivo entre las aceptadas. La rama de paso a mayoría de edad reutiliza estas
     * mismas pantallas con sus propias claves (2.4.2/2.4.3/2.4.4), y el avance debe declarar la
     * real o el backend responde STEP_MISMATCH.
     */
    activeStepKey: string | null;
    /**
     * false cuando se ha entrado por reentrada a un paso ya superado: la pantalla es consultable y
     * editable, pero no puede avanzar el onboarding (el backend respondería STEP_MISMATCH).
     */
    isCurrentStep: boolean;
}

/**
 * Guarda de paso para las pantallas de ContractManagement.
 *
 * Consulta el estado de onboarding del plan al montar y comprueba que el paso esperado
 * (expectedStepKey) sea EXACTAMENTE el paso actual del plan (isCurrent). No basta con que esté
 * "enabled": en la máquina lineal un paso ya superado sigue enabled (significa "los anteriores
 * están completos"), así que un deep-link a una pantalla ya completada pasaría el filtro y su
 * botón "Continuar" avanzaría el paso ACTUAL (que es otro) de forma silenciosa. Por eso exigimos
 * isCurrent: la pantalla accionable solo se muestra cuando el backend dice que ese es el paso vivo.
 * Si no lo es (deep-link, vuelta atrás, o paso no habilitado), redirige al mapa.
 *
 * Excepción: los pasos reentrantes (idoneidad) se aceptan ya superados mientras sigan `enabled`, y
 * se devuelven con isCurrentStep=false para que la pantalla no ofrezca avanzar el onboarding.
 *
 * Mientras resuelve, isChecking es true para que la pantalla muestre el loader.
 *
 * Anti-bucle: el efecto SOLO depende de savingsPlanId y de las claves unidas en un string
 * (primitivos). isChecking NO está en las deps → setIsChecking(true/false) provoca re-render pero
 * NUNCA re-ejecuta el efecto. history y la lista de claves van por ref para que ni un cambio de
 * identidad del objeto history ni el array literal de la pantalla puedan re-disparar la guarda.
 */
export function useContractStepGuard(savingsPlanId: number, expectedStepKeys: string | string[]): ContractStepGuardResult
{
    const history = useHistory();
    const historyRef = useRef(history);
    historyRef.current = history;

    const [isChecking, setIsChecking] = useState(true);
    const [activeStepKey, setActiveStepKey] = useState<string | null>(null);
    const [isCurrentStep, setIsCurrentStep] = useState(false);

    const acceptedStepKeys = Array.isArray(expectedStepKeys) ? expectedStepKeys : [expectedStepKeys];
    const acceptedStepKeysRef = useRef(acceptedStepKeys);
    acceptedStepKeysRef.current = acceptedStepKeys;
    const acceptedStepKeysDep = acceptedStepKeys.join("|");

    useEffect(() =>
    {
        let cancelled = false;

        // Solo se ejecuta al montar o cuando cambian plan/claves (deps abajo).
        // Reinicia el loader para no pintar contenido del plan anterior.
        setIsChecking(true);
        setActiveStepKey(null);
        setIsCurrentStep(false);

        const check = async () =>
        {
            if(Number.isNaN(savingsPlanId))
            {
                // Sin un id de plan válido no podemos verificar nada: a la lista de planes.
                historyRef.current.replace("/savingsPlans");
                return;
            }

            const envelope = await savingsPlans.getPlanOnboardingState(savingsPlanId);

            if(cancelled)
            {
                return;
            }

            if(!envelope.success || !envelope.data)
            {
                // Plan no accesible (403/404) o error: al mapa, que muestra su propio mensaje.
                historyRef.current.replace("/configurationMap", buildMapNavigationState(savingsPlanId));
                return;
            }

            // Se busca directamente el paso vivo entre los aceptados, no el primero que coincida: en la
            // rama de mayoría de edad el plan trae también su paso gemelo del tronco, ya superado.
            const enabledSteps = envelope.data.steps.filter((s) => acceptedStepKeysRef.current.includes(s.key) && s.enabled === true);
            const currentStep = enabledSteps.find((s) => s.isCurrent === true);
            // El paso vivo manda sobre la reentrada: solo él puede avanzar el onboarding.
            const step = currentStep ?? enabledSteps.find((s) => isReentrantContractStepKey(s.key));
            if(step === undefined)
            {
                // Ninguna de las claves aceptadas es el paso actual habilitado del plan ni un paso
                // reentrante (deep-link, vuelta atrás o paso no habilitado): al mapa.
                historyRef.current.replace("/configurationMap", buildMapNavigationState(savingsPlanId));
                return;
            }

            setActiveStepKey(step.key);
            setIsCurrentStep(step.isCurrent === true);
            setIsChecking(false);
        };

        void check();

        return () =>
        {
            cancelled = true;
        };
        // Deps deliberadamente solo plan + claves unidas. No incluir isChecking ni history.
    }, [savingsPlanId, acceptedStepKeysDep]);

    return { isChecking, activeStepKey, isCurrentStep };
}